import { type StoreName } from '@bluebrick/discounts-service-module'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useStoresStore } from '@/features/stores/useStoresStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { allowedMultipleSidebars, SidebarContent, type SkuOption } from '@/types'

const useSidebarInnerStore = defineStore('sidebarInner', () => {
	const visibleSidebars = ref(new Set<SidebarContent>())
	const selectedHistorySKUOption = ref<SkuOption>()
	const dashboardDiscountEnabled = ref(false)

	return {
		visibleSidebars,
		selectedHistorySKUOption,
		dashboardDiscountEnabled
	}
})

export const useSidebarStore = defineStore('sidebar', () => {
	const { trackEvent } = useTelemetry()
	const innerStore = useSidebarInnerStore()
	const { setStoreForFavouriteSKUs } = useStoresStore()

	const isSidebarVisible = computed(() => (type: SidebarContent) => {
		return innerStore.visibleSidebars.has(type)
	})

	const selectedHistorySKUOption = computed(() => innerStore.selectedHistorySKUOption)

	const isDashboardDiscountEnabled = computed(() => innerStore.dashboardDiscountEnabled)

	const isEventSidebarVisible = computed(() => isSidebarVisible.value(SidebarContent.EVENT_SETUP))

	function setDashboardDiscountAccess(value: boolean) {
		innerStore.dashboardDiscountEnabled = value
	}

	function showSidebar(type: SidebarContent, isInternalAction?: boolean) {
		innerStore.visibleSidebars.forEach((sidebar) => {
			if (
				(!(sidebar in allowedMultipleSidebars) || !allowedMultipleSidebars[sidebar]?.includes(type)) &&
				(!(type in allowedMultipleSidebars) || !allowedMultipleSidebars[type]?.includes(sidebar))
			) {
				// Close all other sidebars which are not allowed to be displayed together with the opened one
				closeSidebar(sidebar, true)
			}
		})

		innerStore.visibleSidebars.add(type)

		if (!isInternalAction) {
			trackEvent('user', 'User opened sidebar', { sidebarType: type })
		}
	}

	function closeSidebar(type: SidebarContent, isInternalAction?: boolean) {
		innerStore.visibleSidebars.delete(type)

		if (isInternalAction) {
			trackEvent('app', 'Sidebar was closed', { sidebarType: type })
		} else {
			trackEvent('user', 'User closed sidebar', { sidebarType: type })
		}
	}

	function showEventSidebar() {
		showSidebar(SidebarContent.EVENT_SETUP)
	}

	function closeEventSidebar(isInternalAction?: boolean) {
		closeSidebar(SidebarContent.EVENT_SETUP, isInternalAction)
	}

	function closeFavouriteSKUsSidebar() {
		closeSidebar(SidebarContent.FAVOURITE_SKUS)
	}

	function showProductHistory(skuOption: SkuOption) {
		innerStore.selectedHistorySKUOption = skuOption

		showSidebar(SidebarContent.PRODUCT_HISTORY)
	}

	function showEventTypeLegend() {
		showSidebar(SidebarContent.EVENT_TYPE_LEGEND)
	}

	function showFavouriteSKUs(storeId?: StoreName) {
		setStoreForFavouriteSKUs(storeId)

		showSidebar(SidebarContent.FAVOURITE_SKUS)
	}

	function showGuide() {
		showSidebar(SidebarContent.GUIDE)
	}

	function showSaleDaysSummary() {
		showSidebar(SidebarContent.SALE_DAYS_SUMMARY)
	}

	function closeSaleDaysSummary() {
		closeSidebar(SidebarContent.SALE_DAYS_SUMMARY)
	}

	function showAdvisedActions() {
		showSidebar(SidebarContent.ADVISED_ACTIONS)
	}

	function closeAdvisedActions() {
		closeSidebar(SidebarContent.ADVISED_ACTIONS)
	}

	return {
		showSidebar,
		closeSidebar,
		showEventSidebar,
		closeEventSidebar,
		isEventSidebarVisible,
		closeFavouriteSKUsSidebar,
		showProductHistory,
		setDashboardDiscountAccess,
		showEventTypeLegend,
		showFavouriteSKUs,
		showGuide,
		showSaleDaysSummary,
		closeSaleDaysSummary,
		showAdvisedActions,
		closeAdvisedActions,
		isSidebarVisible,
		isDashboardDiscountEnabled,
		selectedHistorySKUOption
	}
})
